/**
 * Airframe geometry for the vehicle panel.
 *
 * Motor numbers, arm angles and spin directions are transcribed verbatim from
 * ArduPilot's `AP_MotorsMatrix.cpp` motor tables (master, read 2026-08-18).
 * Angles are DEGREES CLOCKWISE FROM THE NOSE — ArduPilot's convention, not
 * screen degrees.
 *
 * Treat this file as transcribed hardware data, not as code. If a diagram puts
 * a motor on the wrong arm, the fix belongs here — checked against
 * https://ardupilot.org/copter/docs/connect-escs-and-motors.html — and never in
 * the renderer.
 *
 * Channel assignments are ArduPilot DEFAULTS (`SERVOn_FUNCTION`), because the
 * log we render from carries no parameters. Copter maps motor N to output N;
 * Plane defaults to aileron/elevator/throttle/rudder on 1–4 with quadplane lift
 * motors on 5–8. A vehicle that was set up differently needs the frame picker,
 * which is why the picker exists.
 *
 * ponytail: channel mapping is assumed, not read. Replace `ch` with the real
 * SERVOn_FUNCTION values the day the API exposes log parameters — see
 * docs/backend-requests.md.
 */

const rad = (deg) => (deg * Math.PI) / 180;

/**
 * ArduPilot angle → unit vector in SVG space (y grows downward), so 0° (nose)
 * points up the screen and 90° points right.
 */
const rotor = (id, ch, deg, spin, extra = {}) => ({
  id,
  ch,
  esc: ch - 1, // ESC log Instance is 0-based on the output index
  deg,
  spin,
  x: +Math.sin(rad(deg)).toFixed(4),
  y: +(-Math.cos(rad(deg))).toFixed(4),
  tier: "single",
  ...extra,
});

const surface = (id, ch, label, x, y, extra = {}) => ({ id, ch, label, x, y, ...extra });

export const FRAMES = {
  quad_x: {
    label: "Quad X",
    kind: "multirotor",
    vehicles: ["Copter", "ArduCopter"],
    motorCount: 4,
    rotors: [
      rotor("M1", 1, 45, "ccw"),
      rotor("M2", 2, -135, "ccw"),
      rotor("M3", 3, -45, "cw"),
      rotor("M4", 4, 135, "cw"),
    ],
  },
  quad_plus: {
    label: "Quad +",
    kind: "multirotor",
    vehicles: ["Copter", "ArduCopter"],
    motorCount: 4,
    rotors: [
      rotor("M1", 1, 90, "ccw"),
      rotor("M2", 2, -90, "ccw"),
      rotor("M3", 3, 0, "cw"),
      rotor("M4", 4, 180, "cw"),
    ],
  },
  hexa_x: {
    label: "Hexa X",
    kind: "multirotor",
    vehicles: ["Copter", "ArduCopter"],
    motorCount: 6,
    rotors: [
      rotor("M1", 1, 90, "cw"),
      rotor("M2", 2, -90, "ccw"),
      rotor("M3", 3, -30, "cw"),
      rotor("M4", 4, 150, "ccw"),
      rotor("M5", 5, 30, "ccw"),
      rotor("M6", 6, -150, "cw"),
    ],
  },
  octa_x: {
    label: "Octa X",
    kind: "multirotor",
    vehicles: ["Copter", "ArduCopter"],
    motorCount: 8,
    rotors: [
      rotor("M1", 1, 22.5, "cw"),
      rotor("M2", 2, -157.5, "cw"),
      rotor("M3", 3, 67.5, "ccw"),
      rotor("M4", 4, 157.5, "ccw"),
      rotor("M5", 5, -22.5, "ccw"),
      rotor("M6", 6, -112.5, "ccw"),
      rotor("M7", 7, -67.5, "cw"),
      rotor("M8", 8, 112.5, "cw"),
    ],
  },
  octaquad_x: {
    // Four arms, coaxial pairs. M1–M4 are the top layer, M5–M8 sit under them
    // on the same arm and counter-rotate against their partner.
    label: "OctaQuad X",
    kind: "multirotor",
    vehicles: ["Copter", "ArduCopter"],
    motorCount: 8,
    rotors: [
      rotor("M1", 1, 45, "ccw", { tier: "top" }),
      rotor("M2", 2, -45, "cw", { tier: "top" }),
      rotor("M3", 3, -135, "ccw", { tier: "top" }),
      rotor("M4", 4, 135, "cw", { tier: "top" }),
      rotor("M5", 5, -45, "ccw", { tier: "bottom" }),
      rotor("M6", 6, 45, "cw", { tier: "bottom" }),
      rotor("M7", 7, 135, "ccw", { tier: "bottom" }),
      rotor("M8", 8, -135, "cw", { tier: "bottom" }),
    ],
  },
  quadplane: {
    // Q_FRAME_CLASS=1 / Q_FRAME_TYPE=1 — the lift rotors are a Quad X, moved to
    // outputs 5–8 because 1–4 carry the plane's surfaces and forward throttle.
    label: "Quadplane (VTOL)",
    kind: "vtol",
    vehicles: ["Plane", "ArduPlane"],
    motorCount: 5,
    rotors: [
      rotor("M1", 5, 45, "ccw"),
      rotor("M2", 6, -135, "ccw"),
      rotor("M3", 7, -45, "cw"),
      rotor("M4", 8, 135, "cw"),
      { ...rotor("FWD", 3, 180, null), forward: true },
    ],
    surfaces: [
      surface("AIL", 1, "Aileron", -0.72, 0.12),
      // Flanking the tailplane rather than stacked on it — the chips are 38
      // units wide and collide if they share a column.
      surface("ELE", 2, "Elevator", -0.28, 0.86),
      surface("RUD", 4, "Rudder", 0.28, 0.86),
    ],
  },
  wing: {
    label: "Flying wing",
    kind: "wing",
    vehicles: ["Plane", "ArduPlane"],
    motorCount: 1,
    rotors: [{ ...rotor("M1", 3, 180, null), forward: true }],
    surfaces: [
      surface("ELVL", 1, "Elevon L", -0.52, 0.52),
      surface("ELVR", 2, "Elevon R", 0.52, 0.52),
    ],
  },
};

export const FRAME_IDS = Object.keys(FRAMES);

/**
 * Best guess at the airframe from what the log actually tells us: the vehicle
 * type and how many motor outputs are live. It is only a default for the
 * picker — nothing downstream should assume it is right.
 */
export const guessFrame = (vehicleType, motorCount) => {
  const isPlane = /plane/i.test(vehicleType || "");
  if (isPlane) return motorCount >= 5 ? "quadplane" : "wing";
  if (motorCount >= 8) return "octa_x";
  if (motorCount >= 6) return "hexa_x";
  return "quad_x";
};

/* ---------------------------------------------------------------- checks ---- */
// This repo has no test runner (see src/utils/panelTokens.js), so the table's
// invariants are asserted on load in dev. A transcription slip here is silent
// and ships a diagram that lies about which motor is hot.
if (process.env.NODE_ENV !== "production") {
  const fail = (msg) => console.error(`frames: ${msg}`);

  for (const [id, frame] of Object.entries(FRAMES)) {
    const chans = frame.rotors.map((r) => r.ch);
    if (new Set(chans).size !== chans.length) fail(`${id} reuses an output channel`);

    for (const r of frame.rotors) {
      const mag = Math.hypot(r.x, r.y);
      if (Math.abs(mag - 1) > 1e-3) fail(`${id}/${r.id} is not on the unit circle (${mag})`);
    }

    const lifters = frame.rotors.filter((r) => !r.forward);

    // Flat multirotors must counter-rotate around the ring, or yaw torque
    // cannot cancel. Coaxial frames alternate per arm instead.
    if (frame.kind === "multirotor" && lifters.every((r) => r.tier === "single")) {
      const ring = [...lifters].sort((a, b) => ((a.deg + 360) % 360) - ((b.deg + 360) % 360));
      for (let i = 1; i < ring.length; i += 1) {
        if (ring[i].spin === ring[i - 1].spin) {
          fail(`${id}: ${ring[i - 1].id} and ${ring[i].id} are adjacent and spin the same way`);
        }
      }
    }

    // Coaxial pairs share an arm angle and must oppose each other.
    const byArm = {};
    for (const r of lifters.filter((x) => x.tier !== "single")) {
      (byArm[r.deg] ??= []).push(r);
    }
    for (const [deg, pair] of Object.entries(byArm)) {
      if (pair.length !== 2) fail(`${id}: arm ${deg}° has ${pair.length} motors, expected a pair`);
      else if (pair[0].spin === pair[1].spin) fail(`${id}: coaxial pair at ${deg}° does not counter-rotate`);
    }
  }
}
